import React, { useMemo } from 'react'
import { CellClickedEvent, ColDef } from 'ag-grid-enterprise'
import { useTranslation } from 'react-i18next'
import { Company } from '../../../types/company/Company'
import CustomAgGrid from '../../../shared/components/custom/Grid/CustomAgGrid'
import { MappingLevel, MappingMatrixRow } from '../types'
import { getMappingLabel, getMappingLevel, MAPPING_LEVEL_COLORS } from '../utils/mappingLevel'
import { mappingGridTheme, MAPPING_GRID_HEADER_HEIGHT, MAPPING_GRID_ROW_HEIGHT } from '../utils/mappingGridTheme'

interface MappingMatrixTableProps {
  companies: Company[]
  rows: MappingMatrixRow[]
  loading?: boolean
  onCellSelect?: (ownerId: string, counterpartyId: string) => void
}

const MappingMatrixTable: React.FC<MappingMatrixTableProps> = ({ companies, rows, loading, onCellSelect }) => {
  const { t } = useTranslation()

  const columnDefs = useMemo<ColDef<MappingMatrixRow>[]>(() => {
    const ownerColumn: ColDef<MappingMatrixRow> = {
      colId: 'owner',
      headerName: t('groupCustomerMapping:matrixOwner', 'Kirjanpitävä yhtiö'),
      pinned: 'left',
      width: 220,
      valueGetter: params => params.data?.company.name,
      cellStyle: { fontWeight: 500 }
    }

    const counterpartyColumns: ColDef<MappingMatrixRow>[] = companies.map(counterparty => ({
      colId: counterparty.id,
      headerName: counterparty.name,
      headerTooltip: counterparty.name,
      width: 90,
      valueGetter: params => {
        const cell = params.data?.cells.find(c => c.counterpartyId === counterparty.id)
        return cell ? getMappingLevel(cell) : 'missing'
      },
      valueFormatter: params => getMappingLabel(params.value as MappingLevel),
      cellStyle: params => ({
        backgroundColor: MAPPING_LEVEL_COLORS[params.value as MappingLevel],
        textAlign: 'center' as const,
        fontSize: 16,
        cursor: params.value === 'self' ? 'default' : 'pointer'
      })
    }))

    return [ownerColumn, ...counterpartyColumns]
  }, [companies, t])

  const handleCellClicked = (event: CellClickedEvent<MappingMatrixRow>) => {
    const counterpartyId = event.column.getColId()
    const ownerId = event.data?.company.id
    if (!onCellSelect || !ownerId || counterpartyId === 'owner' || counterpartyId === ownerId) return
    onCellSelect(ownerId, counterpartyId)
  }

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      filter: false,
      resizable: true,
      suppressHeaderMenuButton: true
    }),
    []
  )

  return (
    <div>
      <CustomAgGrid
        theme={mappingGridTheme}
        columnDefs={columnDefs}
        domLayout="autoHeight"
        defaultColDef={defaultColDef}
        rowData={rows}
        getRowId={params => params.data.company.id}
        rowHeight={MAPPING_GRID_ROW_HEIGHT}
        headerHeight={MAPPING_GRID_HEADER_HEIGHT}
        loading={loading}
        onCellClicked={handleCellClicked}
      />
    </div>
  )
}

export default MappingMatrixTable
